'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'

/**
 * Runs on every client-side route change.
 *
 * Next.js leaves keyboard focus wherever the clicked link was (or nowhere) and
 * only announces the new title through its live region. Research with screen
 * reader, magnification, voice and switch users (Fable Tech Labs, 2019) found
 * the best landing spot after a route change is the skip link itself: a small
 * control that becomes visible on keyboard use and lets the user bypass the
 * header again, exactly as on a fresh page load.
 *
 * - Keyboard user: the skip link takes focus and is visible (:focus-visible).
 * - Mouse user: the browser does not show focus for programmatic focus after a
 *   pointer interaction, so the link would be focused but invisible and the next
 *   Tab would step past it. In that case the tab position is reset to just before
 *   the link instead, so the next Tab reaches it.
 *
 * The very first load is left alone: the browser already starts at the top.
 */
const ScrollToTop = () => {
    const pathname = usePathname()
    const isFirstLoad = useRef(true)

    useEffect(() => {
        window.scrollTo(0, 0)

        if (isFirstLoad.current) {
            isFirstLoad.current = false
            return
        }

        const skipLink = document.querySelector('.skip-link')
        if ( ! skipLink) return

        skipLink.focus({ preventScroll: true })
        if (skipLink.matches(':focus-visible')) return

        // Pointer-driven navigation: park the tab position before the link
        const anchor = document.getElementById('page-top')
        if (anchor) {
            anchor.focus({ preventScroll: true })
            anchor.blur()
        }
    }, [pathname])

    return null
}

export default ScrollToTop
